// ========== 水体渲染系统 - Canvas 焦散光效 + 浑浊流动（性能优化版）==========
import NoiseGenerator from '../utils/noise.js';
import { devicePerformance, targetFPS } from '../utils/performance.js';

export class WaterRenderer {
    constructor(canvasId, sectionId) {
        this.canvas = document.getElementById(canvasId);
        this.section = document.getElementById(sectionId);
        
        if (!this.canvas || !this.section) return;
        
        this.ctx = this.canvas.getContext('2d');
        this.noise = new NoiseGenerator(42);
        
        this.time = 0;
        this.lastTimestamp = 0;
        this.animationId = null;
        this.isPageVisible = true;
        
        // 性能自适应：使用统一的性能检测结果
        this.targetFPS = targetFPS;
        this.frameInterval = 1000 / this.targetFPS;
        this.lastFrameTime = 0;
        this.frameDropCount = 0;
        this.fpsAdjustmentCount = 0;
        
        console.log('🎬 Canvas 初始目标帧率:', this.targetFPS, 'FPS');
        
        this.initConfig();
        this.init();
    }
    
    initConfig() {
        // 焦散光效配置
        this.causticLayers = [
            { scale: 0.008, speed: 0.15, amp: 40, weight: 0.7 },
            { scale: 0.015, speed: 0.20, amp: 25, weight: 0.5 },
            { scale: 0.025, speed: 0.30, amp: 15, weight: 0.3 }
        ];
        
        // 浑浊流动配置（性能优化）
        this.turbidityPatches = [];
        const PATCH_COUNT = devicePerformance === 'low' ? 20 : 35;
        console.log('💧 浑浊团块数量:', PATCH_COUNT);
        
        for (let i = 0; i < PATCH_COUNT; i++) {
            this.turbidityPatches.push({
                x: Math.random(),
                y: Math.random(),
                radius: 0.08 + Math.random() * 0.18,
                turbidity: 0.3 + Math.random() * 0.5,
                speed: 0.3 + Math.random() * 0.5,
                phase: Math.random() * 2 * Math.PI,
                driftX: 0.0001 + Math.random() * 0.0004,
                driftY: 0.0001 + Math.random() * 0.0004
            });
        }
        
        // 采样步长（性能优化）
        this.sampleStep = devicePerformance === 'low' ? 60 : 40;
    }
    
    init() {
        this.resize();
        window.addEventListener('resize', () => this.resize());
        
        let resizeTimeout = null;
        window.addEventListener('zoomwrap:resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => this.resize(), 100);
        });
        
        document.addEventListener('visibilitychange', () => {
            this.isPageVisible = document.visibilityState === 'visible';
            if (this.isPageVisible) {
                this.lastFrameTime = performance.now();
                this.render(this.lastFrameTime);
            } else if (this.animationId) {
                cancelAnimationFrame(this.animationId);
                this.animationId = null;
            }
        });
        
        this.animationId = requestAnimationFrame((t) => this.render(t));
    }
    
    resize() {
        const w = this.section.offsetWidth;
        const h = this.section.offsetHeight;
        if (w <= 0 || h <= 0) return;
        
        const dpr = window.devicePixelRatio || 1;
        this.canvas.width = w * dpr;
        this.canvas.height = h * dpr;
        this.canvas.style.width = w + 'px';
        this.canvas.style.height = h + 'px';
        this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    
    getWidth() {
        return this.canvas.width / (window.devicePixelRatio || 1);
    }
    
    getHeight() {
        return this.canvas.height / (window.devicePixelRatio || 1);
    }
    
    render(timestamp) {
        if (!this.isPageVisible) return;
        
        // 帧率限制
        const elapsed = timestamp - this.lastFrameTime;
        if (elapsed < this.frameInterval) {
            this.animationId = requestAnimationFrame((t) => this.render(t));
            return;
        }
        
        // 掉帧检测与自适应帧率调整
        if (elapsed > this.frameInterval * 1.8) {
            this.frameDropCount++;
            // 连续掉帧10次，降低目标帧率
            if (this.frameDropCount > 10 && this.fpsAdjustmentCount < 3) {
                const oldFPS = this.targetFPS;
                this.targetFPS = Math.max(15, this.targetFPS - 5);
                this.frameInterval = 1000 / this.targetFPS;
                this.frameDropCount = 0;
                this.fpsAdjustmentCount++;
                console.warn('⚠️ Canvas 检测到持续掉帧，降低帧率:', oldFPS, 'FPS →', this.targetFPS, 'FPS');
            }
        } else {
            if (this.frameDropCount > 0) {
                this.frameDropCount = Math.max(0, this.frameDropCount - 1);
            }
        }
        
        this.lastFrameTime = timestamp;
        
        if (!this.lastTimestamp) this.lastTimestamp = timestamp;
        const dt = Math.min((timestamp - this.lastTimestamp) / 1000, 0.05);
        this.lastTimestamp = timestamp;
        this.time += dt;
        
        const w = this.getWidth();
        const h = this.getHeight();
        if (w <= 0 || h <= 0) {
            this.animationId = requestAnimationFrame((t) => this.render(t));
            return;
        }
        
        this.ctx.clearRect(0, 0, w, h);
        
        this.renderCaustics(w, h);
        this.renderSunlight(w, h);
        this.renderTurbidity(w, h);
        this.renderDepthGradient(w, h);
        
        this.animationId = requestAnimationFrame((t) => this.render(t));
    }
    
    renderCaustics(w, h) {
        this.causticLayers.forEach((layer) => {
            const s = layer.scale;
            const spd = layer.speed;
            const amp = layer.amp;
            const alpha = layer.weight * 0.18;
            
            this.ctx.beginPath();
            this.ctx.fillStyle = 'rgba(255, 255, 255, ' + alpha.toFixed(3) + ')';
            
            const step = Math.max(12, amp * 0.8);
            for (let x = -step; x < w + step; x += step) {
                for (let y = -step; y < h + step; y += step) {
                    const nx = x * s;
                    const ny = y * s;
                    const nt = this.time * spd;
                    
                    const wave1 = Math.sin(nx * 0.7 + ny * 1.3 + nt) * 0.5 + 0.5;
                    const wave2 = Math.sin(nx * 1.1 - ny * 0.9 + nt * 0.7) * 0.5 + 0.5;
                    const wave3 = Math.sin((nx + ny) * 0.5 + nt * 1.2) * 0.5 + 0.5;
                    
                    const noiseVal = this.noise.noise2D(nx * 1.5 + nt * 0.2, ny * 1.5 + nt * 0.15) * 0.5 + 0.5;
                    const intensity = (wave1 * wave2 * 0.8 + wave3 * 0.2) * noiseVal;
                    
                    if (intensity > 0.35) {
                        const spotRadius = 2 + intensity * 6;
                        const alpha2 = (intensity - 0.35) * 1.5;
                        
                        this.ctx.globalAlpha = Math.min(alpha2 * alpha * 4, alpha);
                        this.ctx.beginPath();
                        this.ctx.arc(
                            x + this.noise.noise2D(nx * 2 + nt, ny * 2) * 4,
                            y + this.noise.noise2D(nx * 2 + nt + 100, ny * 2) * 4,
                            spotRadius, 0, Math.PI * 2
                        );
                        this.ctx.fill();
                    }
                }
            }
            this.ctx.globalAlpha = 1;
        });
    }
    
    renderSunlight(w, h) {
        this.ctx.save();
        const grad = this.ctx.createRadialGradient(w * 0.5, h * 0.05, 0, w * 0.5, h * 0.05, w * 0.6);
        grad.addColorStop(0, 'rgba(180, 230, 255, ' + (0.06 + 0.03 * Math.sin(this.time * 0.15)) + ')');
        grad.addColorStop(0.3, 'rgba(140, 210, 255, ' + (0.03 + 0.02 * Math.sin(this.time * 0.12)) + ')');
        grad.addColorStop(1, 'rgba(100, 180, 240, 0)');
        this.ctx.fillStyle = grad;
        this.ctx.fillRect(0, 0, w, h);
        
        this.ctx.globalAlpha = 0.015 + 0.01 * Math.sin(this.time * 0.08);
        for (let i = 0; i < 6; i++) {
            const xPos = w * (0.15 + i * 0.14) + Math.sin(this.time * 0.1 + i * 2) * w * 0.05;
            const grad2 = this.ctx.createLinearGradient(xPos, 0, xPos + 20 + Math.sin(this.time * 0.1 + i) * 10, 0);
            grad2.addColorStop(0, 'rgba(200, 235, 255, 0)');
            grad2.addColorStop(0.5, 'rgba(200, 235, 255, 0.15)');
            grad2.addColorStop(1, 'rgba(200, 235, 255, 0)');
            this.ctx.fillStyle = grad2;
            this.ctx.fillRect(xPos - 5, 0, 30, h);
        }
        this.ctx.globalAlpha = 1;
        this.ctx.restore();
    }
    
    renderTurbidity(w, h) {
        this.ctx.save();
        this.turbidityPatches.forEach(patch => {
            const flowX = this.noise.fbm(
                patch.x * 2 + this.time * 0.03 * patch.speed,
                patch.y * 2 + this.time * 0.02 * patch.speed, 2, 2.0, 0.5
            );
            const flowY = this.noise.fbm(
                patch.x * 2 + this.time * 0.02 * patch.speed + 50,
                patch.y * 2 + this.time * 0.03 * patch.speed + 50, 2, 2.0, 0.5
            );
            
            const cx = (patch.x + flowX * 0.02 + this.time * patch.driftX) % 1.0;
            const cy = (patch.y + flowY * 0.02 + this.time * patch.driftY) % 1.0;
            
            const px = cx * w;
            const py = cy * h;
            const radius = patch.radius * Math.max(w, h);
            
            const turbPulse = 0.6 + 0.4 * Math.sin(this.time * 0.08 * patch.speed + patch.phase);
            const currentTurb = patch.turbidity * turbPulse;
            
            const r = Math.round(130 + currentTurb * 80);
            const g = Math.round(160 + currentTurb * 60);
            const b = Math.round(200 + currentTurb * 40);
            
            const gradient = this.ctx.createRadialGradient(px, py, 0, px, py, radius);
            gradient.addColorStop(0, 'rgba(' + r + ', ' + g + ', ' + b + ', ' + (0.08 + currentTurb * 0.12).toFixed(3) + ')');
            gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
            
            this.ctx.fillStyle = gradient;
            this.ctx.beginPath();
            this.ctx.arc(px, py, radius, 0, Math.PI * 2);
            this.ctx.fill();
        });
        
        // 额外的噪波浑浊层（使用动态采样步长）
        for (let y = 0; y < h; y += this.sampleStep) {
            for (let x = 0; x < w; x += this.sampleStep) {
                const nx = x * 0.002;
                const ny = y * 0.002;
                const nt = this.time * 0.015;
                
                let turbVal = this.noise.fbm(nx + nt * 0.1, ny + nt * 0.08, 2, 2.2, 0.55);
                turbVal = (turbVal + 1) * 0.5;
                
                if (turbVal > 0.52) {
                    const alpha = (turbVal - 0.52) * 0.25;
                    this.ctx.fillStyle = 'rgba(180, 205, 235, ' + alpha.toFixed(3) + ')';
                    this.ctx.fillRect(x, y, this.sampleStep, this.sampleStep);
                }
            }
        }
        this.ctx.restore();
    }
    
    renderDepthGradient(w, h) {
        this.ctx.save();
        const depthGrad = this.ctx.createLinearGradient(0, 0, 0, h);
        depthGrad.addColorStop(0, 'rgba(180, 220, 255, 0.04)');
        depthGrad.addColorStop(0.3, 'rgba(140, 200, 240, 0.02)');
        depthGrad.addColorStop(0.6, 'rgba(60, 120, 180, 0.02)');
        depthGrad.addColorStop(1, 'rgba(20, 50, 90, 0.06)');
        this.ctx.fillStyle = depthGrad;
        this.ctx.fillRect(0, 0, w, h);
        this.ctx.restore();
    }
}

export function initWaterRenderer() {
    new WaterRenderer('waterCanvas', 'textureSection');
}
